import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { StaffMember, updateStaffMember } from '@/services/staffService';
import { uploadStaffPhoto } from '@/services/staff/staffPhotoService';

interface StaffFormData {
  first_name: string | undefined;
  last_name: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  role: string | undefined;
  start_date: string | undefined;
  location_id: string | undefined;
  is_active: boolean | undefined;
  birthday_month: number | undefined;
  birthday_day: number | undefined;
  profile_photo_url: string | undefined;
  address_line1: string | undefined;
  address_line2: string | undefined;
  city: string | undefined;
  state: string | undefined;
  zip: string | undefined;
  notes: string | undefined;
}

interface UseStaffFormProps {
  staff: StaffMember;
  onUpdated: (staff: StaffMember) => void;
}

export const useStaffForm = ({ staff, onUpdated }: UseStaffFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<StaffFormData>({
    first_name: staff.first_name || '',
    last_name: staff.last_name || '',
    email: staff.email || '',
    phone: staff.phone || '',
    role: staff.role || '',
    start_date: staff.start_date || '',
    location_id: staff.location_id || '',
    is_active: staff.is_active !== false,
    birthday_month: staff.birthday_month || undefined,
    birthday_day: staff.birthday_day || undefined,
    profile_photo_url: staff.profile_photo_url || '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: '',
    notes: ''
  });

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleSwitchChange = useCallback((name: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  }, []);

  const handleImageUpload = useCallback(async (file: File) => {
    try {
      const photoUrl = await uploadStaffPhoto(file, staff.id);
      if (photoUrl) {
        // Update form data with new photo URL
        setFormData(prev => ({
          ...prev,
          profile_photo_url: photoUrl
        }));

        // Auto-save the staff member with the new photo
        const updateData = { profile_photo_url: photoUrl };
        console.log('Auto-saving staff photo:', updateData);
        
        const updatedStaff = await updateStaffMember(staff.id, updateData);
        
        if (updatedStaff) {
          toast.success('Photo uploaded and saved successfully!');
          onUpdated(updatedStaff);
        } else {
          toast.error('Photo uploaded but failed to save to profile');
        }
      } else {
        toast.error('Failed to upload photo');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photo');
    }
  }, [staff.id, onUpdated]);

  const saveStaff = useCallback(async () => {
    try {
      setIsSubmitting(true);

      // Create update payload with only changed fields
      const updateData: Record<string, any> = {};
      
      // Check for changes and add to update payload
      if (formData.first_name !== staff.first_name) {
        updateData.first_name = formData.first_name;
      }
      if (formData.last_name !== staff.last_name) {
        updateData.last_name = formData.last_name;
      }
      if (formData.email !== staff.email) {
        updateData.email = formData.email || null;
      }
      if (formData.phone !== staff.phone) {
        updateData.phone = formData.phone || null;
      }
      if (formData.role !== staff.role) {
        updateData.role = formData.role;
      }
      if (formData.start_date !== staff.start_date) {
        updateData.start_date = formData.start_date || null;
      }
      if (formData.is_active !== staff.is_active) {
        updateData.is_active = formData.is_active;
      }
      if (formData.birthday_month !== staff.birthday_month) {
        updateData.birthday_month = formData.birthday_month || null;
      }
      if (formData.birthday_day !== staff.birthday_day) {
        updateData.birthday_day = formData.birthday_day || null;
      }
      if (formData.profile_photo_url !== staff.profile_photo_url) {
        updateData.profile_photo_url = formData.profile_photo_url || null;
      }

      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        return;
      }

      console.log('Updating staff with data:', updateData);
      
      const updatedStaff = await updateStaffMember(staff.id, updateData);
      
      if (updatedStaff) {
        toast.success('Staff member updated successfully');
        onUpdated(updatedStaff);
      }
    } catch (error) {
      console.error('Error updating staff member:', error);
      toast.error('Failed to update staff member', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, staff, onUpdated]);

  return {
    formData,
    isSubmitting,
    handleChange,
    handleSwitchChange,
    handleImageUpload,
    saveStaff
  };
};